import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  listInviteCodes, createInviteCode, deleteInviteCode, getInviteCodeScheme,
  formatUnitLabel, expiresIn, type PublicInviteCode, type AddressScheme,
} from '../../lib/invite-codes-api';
import { listManageableClusters } from '../../lib/accounts-api';
import { deriveUnitLabel, type ManageableClustersResponse } from '@livinhome/shared';
import { SegmentInput } from '../../components/SegmentInput';
import { extractApiError, extractApiErrorDetails } from '../../lib/api-client';
import { useT } from '../../i18n';

type Dev = ManageableClustersResponse['developers'][number];

export function ResidentsPage() {
  const t = useT();
  const [devs, setDevs] = useState<Dev[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ id: string; name: string } | null>(null);
  const [codes, setCodes] = useState<PublicInviteCode[]>([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState<string | null>(null);
  const [deletingCode, setDeletingCode] = useState<PublicInviteCode | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  async function confirmDeleteCode() {
    if (!selected || !deletingCode) return;
    setDeleteBusy(true); setError(null);
    try {
      await deleteInviteCode(selected.id, deletingCode.id);
      setDeletingCode(null);
      await refreshCodes();
    } catch (e) { setError(extractApiError(e).message); } finally { setDeleteBusy(false); }
  }

  async function selectCluster(cl: { id: string; name: string }) {
    setSelected(cl); setCodesLoading(true);
    try { setCodes((await listInviteCodes(cl.id)).items); } catch (e) { setError(extractApiError(e).message); } finally { setCodesLoading(false); }
  }
  async function refreshCodes() { if (selected) await selectCluster(selected); }

  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const ds = await listManageableClusters();
        setDevs(ds);
        const all = ds.flatMap((d) => d.clusters);
        if (all.length === 1) await selectCluster({ id: all[0].id, name: all[0].name });
      } catch (e) { setError(extractApiError(e).message); } finally { setLoading(false); }
    })();
  }, []);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={600}>{t('invite.title')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('invite.subtitle')}</Typography>
      </Box>

      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}

      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : devs.length === 0 ? (
        <Card variant="outlined"><CardContent><Typography color="text.secondary">{t('cadmin.noClusters')}</Typography></CardContent></Card>
      ) : (
        devs.map((d) => (
          <Card key={d.id} variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: d.clusters.length ? 1 : 0 }}>
                <Typography variant="h6">{d.name}</Typography>
                <Chip size="small" label={t('invite.clustersChip', { count: d.clusters.length, plural: d.clusters.length === 1 ? '' : 's' })} />
              </Stack>
              {d.clusters.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('cadmin.noClusterYet')}</Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {d.clusters.map((cl) => (
                    <Button key={cl.id} size="small" variant={selected?.id === cl.id ? 'contained' : 'outlined'} onClick={() => selectCluster({ id: cl.id, name: cl.name })}>
                      {cl.name}
                    </Button>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {selected ? (
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">{t('invite.codesTitle', { name: selected.name })}</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreating(true)}>{t('invite.createBtn')}</Button>
            </Stack>
            {codesLoading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
            ) : codes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t('invite.empty')}</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>{t('invite.colCode')}</TableCell><TableCell>{t('res.colUnit')}</TableCell><TableCell>{t('invite.colExpiresIn')}</TableCell><TableCell>{t('invite.colUses')}</TableCell><TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {codes.map((code) => (
                      <TableRow key={code.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{code.code}</TableCell>
                        <TableCell>{formatUnitLabel(code)}</TableCell>
                        <TableCell>{expiresIn(code.expires_at)}</TableCell>
                        <TableCell>{code.uses_count}/{code.max_uses}</TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('invite.deleteCodeTooltip')}>
                            <Button size="small" color="error" startIcon={<DeleteIcon fontSize="small" />}
                              onClick={() => setDeletingCode(code)}>
                              {t('common.delete')}
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      ) : null}

      <CreateCodeDialog
        open={creating}
        clusterId={selected?.id ?? ''}
        clusterName={selected?.name ?? ''}
        onClose={() => setCreating(false)}
        onCreated={(code) => { setCreating(false); setNewCode(code); refreshCodes(); }}
      />

      <Dialog open={Boolean(deletingCode)} onClose={() => !deleteBusy && setDeletingCode(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('invite.deleteCodeTitle', { code: deletingCode?.code })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deletingCode?.unit_label
              ? t('invite.deleteCodeWarningUnit', { unit: deletingCode.unit_label })
              : t('invite.deleteCodeWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingCode(null)} disabled={deleteBusy}>{t('common.cancel')}</Button>
          <Button onClick={confirmDeleteCode} color="error" variant="contained" disabled={deleteBusy}>
            {deleteBusy ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(newCode)} onClose={() => setNewCode(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('invite.createdTitle')}</DialogTitle>
        <DialogContent>
          <Alert severity="success">{t('invite.colCode')}: <strong style={{ fontFamily: 'monospace', fontSize: '1.2em' }}>{newCode}</strong></Alert>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {t('invite.shareNote')}
          </Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setNewCode(null)}>{t('common.done')}</Button></DialogActions>
      </Dialog>
    </Stack>
  );
}

function CreateCodeDialog({ open, clusterId, clusterName, onClose, onCreated }: {
  open: boolean; clusterId: string; clusterName: string; onClose: () => void; onCreated: (code: string) => void;
}) {
  const t = useT();
  const [scheme, setScheme] = useState<AddressScheme | null>(null);
  const [street, setStreet] = useState('');
  const [block, setBlock] = useState('');
  const [number, setNumber] = useState('');
  // Bumped each time the dialog opens to remount the SegmentInputs (clears them).
  const [formKey, setFormKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capacityWarning, setCapacityWarning] = useState<string | null>(null);
  const [existingCode, setExistingCode] = useState<{ code: string; uses_count: number; max_uses: number; expires_at: string | null } | null>(null);
  // Confirm flags accumulate so Replace + capacity pop-ups can stack across re-submits.
  const [pendingFlags, setPendingFlags] = useState<{ confirm_over_capacity?: boolean; replace_existing?: boolean }>({});

  useEffect(() => {
    if (!open || !clusterId) return;
    setError(null); setCapacityWarning(null); setExistingCode(null); setPendingFlags({});
    setStreet(''); setBlock(''); setNumber('');
    setFormKey((k) => k + 1); setScheme(null); setLoading(true);
    getInviteCodeScheme(clusterId)
      .then(setScheme)
      .catch((e) => setError(extractApiError(e).message))
      .finally(() => setLoading(false));
  }, [open, clusterId]);

  const label = deriveUnitLabel({ street, block, number });
  const canSave =
    Boolean(scheme) && number.trim().length > 0 &&
    (!scheme?.addr_block_enabled || block.trim().length > 0);

  async function submit(flags: { confirm_over_capacity?: boolean; replace_existing?: boolean }) {
    if (!scheme) return;
    setBusy(true); setError(null); setCapacityWarning(null); setExistingCode(null);
    try {
      const code = await createInviteCode(clusterId, {
        ...(scheme.addr_street_enabled && street.trim() ? { street } : {}),
        ...(scheme.addr_block_enabled ? { block } : {}),
        number,
        ...flags,
      });
      setCapacityWarning(null); setExistingCode(null); setPendingFlags({});
      onCreated(code.code);
    } catch (e) {
      const err = extractApiError(e);
      // Soft warnings — surface the relevant pop-up and remember the flags so the
      // confirm re-submit carries any earlier confirmations too.
      if (err.code === 'ERR_UNIT_HAS_ACTIVE_CODE') {
        const d = extractApiErrorDetails(e) as
          | { code: string; uses_count: number; max_uses: number; expires_at: string | null }
          | undefined;
        if (d) setExistingCode(d);
        setPendingFlags(flags);
      } else if (err.code === 'ERR_UNIT_AT_CAPACITY') {
        setCapacityWarning(err.message);
        setPendingFlags(flags);
      } else setError(err.message);
    } finally { setBusy(false); }
  }

  return (
    <>
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{t('invite.dialogTitle', { cluster: clusterName })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {loading || !scheme ? (
            <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('invite.buildAddress')}
              </Typography>
              {scheme.addr_street_enabled ? (
                <SegmentInput key={`street-${formKey}`} label={t('invite.segStreet')} onChange={setStreet} />
              ) : null}
              {scheme.addr_block_enabled ? (
                <SegmentInput key={`block-${formKey}`} label={t('invite.segBlock')} required onChange={setBlock} />
              ) : null}
              <SegmentInput key={`number-${formKey}`} label={t('invite.segNumber')} required onChange={setNumber} />
              <Alert severity="info" icon={false}>
                {t('invite.unitPrefix')}: <strong>{label || '—'}</strong>
              </Alert>
            </>
          )}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button onClick={() => submit({})} variant="contained" disabled={busy || loading || !canSave}>{busy ? t('acc.creating') : t('common.create')}</Button>
      </DialogActions>
    </Dialog>

    <Dialog open={Boolean(existingCode)} onClose={() => !busy && setExistingCode(null)} maxWidth="xs" fullWidth>
      <DialogTitle>{t('invite.existingCodeTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t('invite.existingCodeBody')}
          </Typography>
          {existingCode ? (
            <Alert severity="info" icon={false}>
              <strong style={{ fontFamily: 'monospace', fontSize: '1.2em' }}>{existingCode.code}</strong>
              {' · '}{t('invite.codeUsed', { used: existingCode.uses_count, max: existingCode.max_uses })}
              {' · '}{t('invite.codeExpires', { expires: expiresIn(existingCode.expires_at) })}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setExistingCode(null)} disabled={busy}>{t('common.close')}</Button>
        <Button onClick={() => submit({ ...pendingFlags, replace_existing: true })} variant="contained" color="warning" disabled={busy}>
          {busy ? t('invite.replacing') : t('invite.replaceWithNew')}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={Boolean(capacityWarning)} onClose={() => !busy && setCapacityWarning(null)} maxWidth="xs" fullWidth>
      <DialogTitle>{t('invite.unitFullTitle')}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" icon={false}>{capacityWarning}</Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCapacityWarning(null)} disabled={busy}>{t('common.cancel')}</Button>
        <Button onClick={() => submit({ ...pendingFlags, confirm_over_capacity: true })} variant="contained" color="warning" disabled={busy}>
          {busy ? t('invite.generating') : t('invite.generateAnyway')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
